import React from 'react';
import {
	Box,
	Flex,
	Text,
	VStack,
	HStack,
	Avatar,
	Button,
	Select,
	Input,
	InputGroup,
	InputLeftElement,
	InputRightElement,
	Icon,
	IconButton,
	Skeleton,
	SkeletonCircle,
	Badge,
} from '@chakra-ui/react';
import CustomTooltip from 'components/shared/CustomTooltip';
import { FiSearch, FiEdit, FiX, FiCheck, FiBookmark, FiArchive } from 'react-icons/fi';
import { conversationTitle, matchesSearch } from './phone';
import { stamp } from './time';
import { previewText } from './templateText';
import { useListConversationsQuery, useListChannelsQuery } from 'api/metaWhatsappApi';
import { usePermissions } from 'hooks/usePermissions';
import NewChatDialog from './NewChatDialog';
import FilterChips from './FilterChips';
import { usePrefs, LABELS } from '../hooks/useLocalPrefs';
import useCurrentUserId from '../hooks/useCurrentUserId';
import { useTyping } from '../hooks/useTyping';

/**
 * The inbox list, the way WhatsApp draws it: a pill search, a row of filter
 * chips, then avatar · name · last message · time · unread pill, with the open
 * thread highlighted and pinned threads first.
 *
 * ★ The channel filter is populated from the SERVER, not the client permission
 * map — that snapshot is captured at login and a channel assigned five minutes
 * ago would be missing from it.
 *
 * ★ Pins, archive, labels and drafts are per-browser (useLocalPrefs) until the
 * server has fields for them; the tooltips say "on this device".
 *
 * An empty list is not an error: permissions ship denied, so "nothing here" is
 * the expected state until someone is granted access, and the copy says so
 * rather than looking broken.
 */

/**
 * Was the last message ours? The list row carries no direction, but an
 * outbound message moves `lastMessageAt` without touching `lastInboundAt`
 * (only the customer reopens the window), so the comparison is exact.
 */
const lastIsOurs = (c) =>
	Boolean(c.lastMessageAt) &&
	(!c.lastInboundAt || new Date(c.lastMessageAt) > new Date(c.lastInboundAt));

/** A tiny double-tick for "we wrote last", drawn in the list's muted ink. */
function PreviewTick() {
	return (
		<HStack spacing="0" mr="1" flexShrink={0} aria-hidden="true">
			<Icon as={FiCheck} boxSize="3" color="wa.inkMuted" />
			<Icon as={FiCheck} boxSize="3" color="wa.inkMuted" ml="-2" />
		</HStack>
	);
}

/**
 * One conversation in a list. Exported because the lead drawer shows the same
 * row when a lead has several threads — one look for "a conversation"
 * everywhere, rather than a second, poorer copy.
 */
export function ConversationRow({ conversation: c, active, onSelect, prefs: p, labelsOn }) {
	const unread = c.unreadCount > 0;
	const name = conversationTitle(c);
	const typing = useTyping(c._id);
	const draft = p?.drafts?.[c._id];
	const pinned = Boolean(p?.pinned?.[c._id]);
	const archived = Boolean(p?.archived?.[c._id]);
	const labels = (p?.labels?.[c._id] ?? []).map((id) => LABELS.find((l) => l.id === id)).filter(Boolean);

	let preview;
	if (typing) {
		preview = (
			<Text fontSize="sm" color="wa.accent" noOfLines={1}>
				typing…
			</Text>
		);
	} else if (draft && !active) {
		preview = (
			<Text fontSize="sm" color="wa.inkMuted" noOfLines={1}>
				<Text as="span" color="danger">
					Draft:{' '}
				</Text>
				{draft}
			</Text>
		);
	} else {
		preview = (
			<HStack spacing="0" minW="0">
				{lastIsOurs(c) && c.lastMessagePreview && <PreviewTick />}
				<Text
					fontSize="sm"
					color={unread ? 'wa.ink' : 'wa.inkMuted'}
					fontWeight={unread ? 'medium' : 'normal'}
					noOfLines={1}
				>
					{previewText(c.lastMessagePreview) || 'No messages yet'}
				</Text>
			</HStack>
		);
	}

	return (
		<Flex
			role="listitem"
			px="3"
			align="stretch"
			cursor="pointer"
			bg={active ? 'wa.selected' : 'transparent'}
			opacity={archived ? 0.7 : 1}
			transition="background 120ms ease"
			_hover={{ bg: active ? 'wa.selected' : 'wa.hover' }}
			onClick={() => onSelect(c._id)}
			aria-current={active ? 'true' : undefined}
		>
			<Avatar
				w="49px"
				h="49px"
				name={name}
				mr="3"
				my="2"
				flexShrink={0}
				bg="wa.chip"
				color="wa.inkMuted"
				fontWeight="medium"
			/>
			{/* The divider is inset to the text column — WhatsApp's rows are
			    separated from the name, not from the avatar. */}
			<Box flex="1" minW="0" py="2.5" borderBottomWidth="1px" borderColor="wa.divider">
				<HStack justify="space-between" spacing="2" align="baseline">
					<Text
						fontSize="md"
						fontWeight={unread ? 'semibold' : 'normal'}
						color="wa.ink"
						noOfLines={1}
					>
						{name}
					</Text>
					<Text
						fontSize="xs"
						color={unread ? 'wa.accent' : 'wa.inkMuted'}
						fontWeight={unread ? 'medium' : 'normal'}
						whiteSpace="nowrap"
						flexShrink={0}
					>
						{stamp(c.lastMessageAt)}
					</Text>
				</HStack>
				<HStack justify="space-between" spacing="2" mt="0.5" align="center">
					<Box minW="0" flex="1">
						{preview}
					</Box>
					<HStack spacing="1.5" flexShrink={0}>
						{pinned && (
							<Icon as={FiBookmark} boxSize="14px" color="wa.inkMuted" aria-label="Pinned" />
						)}
						{archived && (
							<Icon as={FiArchive} boxSize="14px" color="wa.inkMuted" aria-label="Archived" />
						)}
						{unread && (
							<Flex
								minW="20px"
								h="20px"
								px="1.5"
								align="center"
								justify="center"
								borderRadius="full"
								bg="wa.accent"
								color="wa.accentInk"
								fontSize="0.7rem"
								fontWeight="bold"
							>
								{c.unreadCount > 99 ? '99+' : c.unreadCount}
							</Flex>
						)}
					</HStack>
				</HStack>
				{(labels.length > 0 || (labelsOn !== false && c.channel?.label)) && (
					<HStack spacing="1" mt="1" minW="0">
						{labels.map((l) => (
							<Badge
								key={l.id}
								colorScheme={l.scheme}
								variant="subtle"
								fontSize="0.6rem"
								borderRadius="sm"
								textTransform="none"
							>
								{l.name}
							</Badge>
						))}
						{/* Which of the tenant's numbers this thread belongs to. With
						    several numbers, the same customer can appear twice. */}
						{labelsOn !== false && c.channel?.label && (
							<Text fontSize="0.65rem" color="wa.inkMuted" noOfLines={1}>
								{c.channel.label}
							</Text>
						)}
					</HStack>
				)}
			</Box>
		</Flex>
	);
}

function RowSkeleton() {
	return (
		<HStack px="3" py="3" spacing="3">
			<SkeletonCircle size="12" startColor="wa.chip" endColor="wa.header" />
			<VStack align="stretch" flex="1" spacing="2">
				<Skeleton h="12px" w="55%" startColor="wa.chip" endColor="wa.header" speed={1.2} />
				<Skeleton h="10px" w="80%" startColor="wa.chip" endColor="wa.header" speed={1.2} />
			</VStack>
		</HStack>
	);
}

export default function ConversationList({ selectedId, onSelect }) {
	const [channel, setChannel] = React.useState('');
	const [filter, setFilter] = React.useState('all');
	const [newChatOpen, setNewChatOpen] = React.useState(false);
	const { hasPermission } = usePermissions();
	// Convenience only — the server refuses regardless, and this map is stale
	// until a reload.
	const canStart = hasPermission('whatsapp', 'conversation.start');
	const [search, setSearch] = React.useState('');
	const p = usePrefs();
	const me = useCurrentUserId();

	const { data: channels = [] } = useListChannelsQuery();
	const { data, isLoading, isError } = useListConversationsQuery(channel ? { channel } : {});

	const conversations = React.useMemo(() => data?.conversations ?? [], [data]);

	const counts = React.useMemo(
		() => ({
			unread: conversations.filter((c) => c.unreadCount > 0).length,
			mine: me ? conversations.filter((c) => String(c.owner) === me).length : 0,
			archived: conversations.filter((c) => p.archived[c._id]).length,
		}),
		[conversations, me, p.archived],
	);

	// Filtering by name happens client-side over the page already loaded; the
	// server owns which conversations exist at all. Pinned threads float to the
	// top, the way WhatsApp keeps them.
	const visible = React.useMemo(() => {
		const needle = search.trim().toLowerCase();
		const list = conversations.filter((c) => {
			if (needle && !matchesSearch(c, needle)) return false;
			const archived = Boolean(p.archived[c._id]);
			if (filter === 'archived') return archived;
			if (archived && !needle) return false;
			if (filter === 'unread') return c.unreadCount > 0;
			if (filter === 'mine') return me && String(c.owner) === me;
			return true;
		});
		return list.sort((a, b) => {
			const pa = p.pinned[a._id] ? 1 : 0;
			const pb = p.pinned[b._id] ? 1 : 0;
			if (pa !== pb) return pb - pa;
			return 0; // the server's ordering (most recent first) otherwise
		});
	}, [conversations, search, filter, p.archived, p.pinned, me]);

	return (
		<Flex direction="column" h="100%" bg="wa.app" borderRightWidth="1px" borderColor="wa.divider">
			<Box pt="3" pb="1">
				<HStack justify="space-between" px="3" mb="2.5">
					<Text fontSize="xl" fontWeight="bold" color="wa.ink">
						Chats
					</Text>
					<HStack spacing="1">
						{canStart && (
							<CustomTooltip label="New chat" placement="bottom" hasArrow>
								<IconButton
									aria-label="New chat"
									size="md"
									variant="ghost"
									borderRadius="full"
									color="wa.ink"
									_hover={{ bg: 'wa.hover' }}
									icon={<Icon as={FiEdit} boxSize="24px" />}
									onClick={() => setNewChatOpen(true)}
								/>
							</CustomTooltip>
						)}
					</HStack>
				</HStack>

				<Box px="3" mb="2">
					<InputGroup size="sm">
						<InputLeftElement pointerEvents="none" h="36px">
							<Icon as={FiSearch} color="wa.inkMuted" />
						</InputLeftElement>
						<Input
							placeholder="Search or start a new chat"
							value={search}
							onChange={(e) => setSearch(e.target.value)}
							bg="wa.chip"
							border="none"
							h="36px"
							borderRadius="full"
							color="wa.ink"
							_placeholder={{ color: 'wa.inkMuted' }}
							_focus={{ boxShadow: '0 0 0 1px var(--chakra-colors-wa-accent)' }}
							aria-label="Search conversations"
						/>
						{search && (
							<InputRightElement h="36px">
								<IconButton
									aria-label="Clear search"
									size="xs"
									variant="ghost"
									borderRadius="full"
									icon={<Icon as={FiX} />}
									onClick={() => setSearch('')}
								/>
							</InputRightElement>
						)}
					</InputGroup>
				</Box>

				<FilterChips
					value={filter}
					onChange={setFilter}
					counts={counts}
					show={{ mine: Boolean(me), archived: counts.archived > 0 || filter === 'archived' }}
				/>

				{channels.length > 1 && (
					<Box px="3" pb="2">
						<Select
							size="xs"
							value={channel}
							onChange={(e) => setChannel(e.target.value)}
							bg="wa.chip"
							border="none"
							borderRadius="md"
							color="wa.ink"
							aria-label="Filter by number"
						>
							<option value="">All numbers</option>
							{channels.map((c) => (
								<option key={c.phoneNumberId} value={c.phoneNumberId}>
									{c.label || c.displayPhoneNumber || c.phoneNumberId}
								</option>
							))}
						</Select>
					</Box>
				)}
			</Box>

			<Box flex="1" overflowY="auto" role="list" aria-label="Conversations">
				{isLoading && (
					<>
						<RowSkeleton />
						<RowSkeleton />
						<RowSkeleton />
						<RowSkeleton />
						<RowSkeleton />
						<RowSkeleton />
					</>
				)}

				{isError && (
					<Box p="4">
						<Text fontSize="sm" color="danger">
							Conversations could not be loaded.
						</Text>
					</Box>
				)}

				{!isLoading && !isError && visible.length === 0 && (
					<Box p="6" textAlign="center">
						<Text fontSize="sm" color="wa.inkMuted">
							{search
								? 'No chats match that search.'
								: filter === 'unread'
									? 'No unread chats.'
									: filter === 'mine'
										? 'You are not handling any chats.'
										: filter === 'archived'
											? 'No archived chats on this device.'
											: 'No conversations yet.'}
						</Text>
						{!search && filter === 'all' && (
							<Text fontSize="xs" color="wa.inkMuted" mt="1">
								They appear here once a customer messages a number you are assigned to.
							</Text>
						)}
						{/* An empty inbox is where the way out matters most — the header
						    icon is easy to miss when there is nothing else on screen. */}
						{!search && filter === 'all' && canStart && (
							<Button
								mt="4"
								size="sm"
								bg="wa.accent"
								color="wa.accentInk"
								_hover={{ bg: 'wa.accentHover' }}
								borderRadius="full"
								leftIcon={<Icon as={FiEdit} />}
								onClick={() => setNewChatOpen(true)}
							>
								Start a new chat
							</Button>
						)}
					</Box>
				)}

				{visible.map((c) => (
					<ConversationRow
						key={c._id}
						conversation={c}
						active={String(c._id) === String(selectedId)}
						onSelect={onSelect}
						prefs={p}
						labelsOn={channels.length > 1}
					/>
				))}
			</Box>

			{/* Mounted here rather than in the page so both entry points — the
			    header icon and the empty-state button — open the same one. */}
			<NewChatDialog
				isOpen={newChatOpen}
				onClose={() => setNewChatOpen(false)}
				onStarted={(conversationId) => onSelect(conversationId)}
			/>
		</Flex>
	);
}
